import {forwardRef} from 'react';

import {Skeleton, styled, Box} from '@mui/material';

import {FrameStyles, ImageCardProps} from './types';
import {useImageCard} from './use-image-card';

const RootWrapper = styled(Box)(() => ({
  display: 'flex',
  flexGrow: 1,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
  height: '100%'
})) as typeof Box;

const ImageWrapper = styled(Box)(() => ({
  flexGrow: 1,
  width: '100%',
  maxWidth: '100%',
  position: 'relative',
  height: '100%',
  objectFit: 'contain',
  '&.marketingBadgeV2': {
    width: '70%',
    maxWidth: '70%',
    height: '70%'
  }
})) as typeof Box;

const ChildrenWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showFrame' && prop !== 'frameStyles'
})<{
  showFrame: boolean;
  frameStyles?: FrameStyles;
}>(({showFrame, frameStyles}) => ({
  flexGrow: 1,
  width: '100%',
  position: 'absolute',
  height: '100%',
  ...(showFrame ? frameStyles || {} : {})
}));

const ImageCard = forwardRef<HTMLImageElement, ImageCardProps>((props, ref) => {
  const {
    domRef,
    getRootProps,
    skeletonLoading,
    borderRadius,
    component,
    children,
    aspectRatio,
    showFrame = false,
    frameStyles = {},
    isMarketingBadgeV2,
    ...otherProps
  } = useImageCard({
    ...props,
    ref
  });

  return (
    <RootWrapper
      ref={domRef}
      {...getRootProps}
      component={'div'}
      data-testid="image-element-root"
      sx={{
        borderRadius: borderRadius == 'none' ? 0 : 2,
        aspectRatio: aspectRatio || '4/3'
      }}
    >
      {skeletonLoading ? (
        <Skeleton height={'100%'} width={'100%'} variant="rounded" />
      ) : (
        <ImageWrapper
          ref={domRef}
          data-testid="image-element"
          component={component || 'img'}
          className={isMarketingBadgeV2 ? 'marketingBadgeV2' : ''}
          {...otherProps}
        />
      )}
      {children && (
        <ChildrenWrapper data-testid="image-element-children-root" frameStyles={frameStyles} showFrame={showFrame}>
          {children}
        </ChildrenWrapper>
      )}
    </RootWrapper>
  );
});

ImageCard.displayName = 'MakroUI.ImageCard';

export default ImageCard;
